import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  IconButton,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import Logo from "../../assets/logo/logo.svg";
import LogoDark from "../../assets/logo/logo_dark.svg";
import MenuIcon from "@mui/icons-material/Menu";
import { menuItems } from "../../utils/menuItems";
import { getColors } from "../../themes/theme";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";

export const Sidebar = () => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode } = useContext(ThemeContext);
  const isMobile = useMediaQuery("(max-width:900px)"); // Check if screen width is less than 768px
  const [drawerOpen, setDrawerOpen] = useState(false); // State to manage Drawer visibility

  const [showSidebar, setShowSidebar] = useState(true);
  useEffect(() => {
    if (user?.licenseAcceptance === 0) {
      setShowSidebar(false);
    }
  }, [user]);

  const currentSection = location.pathname.split("/")[1];

  const handleNavigation = (item) => {
    if (item.id === "wall_art") {
      window.open(`/webgl${item.route}`, "_blank");
    } else if (item.id === "videos") {
      navigate(item.route);
    } else if (item.id === "hip") {
      navigate(`${item.route}${item.route}/Hip`);
    } else if (item.id === "jaw") {
      navigate(`${item.route}${item.route}/Jaw`);
    } else {
      navigate(
        item.id === "spine" ||
          item.id === "nervous_system" ||
          item.id === "treatments"
          ? item.route
          : `${item.route}${item.route}`
      );
    }
    setDrawerOpen(false); // Close the Drawer after navigation
  };

  if (isMobile) {
    // Render Navbar for mobile screens
    return (
      <div style={{ display: showSidebar ? "block" : "none" }}>
        <AppBar
          position="fixed"
          sx={{
            bgcolor: darkMode ? "#1D1E21" : "#FFFFFF",
            width: "50px",
            boxShadow: 0,
            borderRight: darkMode ? "1px solid #2A2A2A" : "1px solid #E8E8E8",
            left: 0,
            top: 0,
            bottom: 0,
          }}
        >
          <Toolbar
            sx={{
              display: "flex",
              justifyContent: "space-between",
              pl: { xs: "24px !important", sm: 2 },
            }}
          >
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              sx={{ mt: { xs: 4 }, padding: 0 }}
              onClick={() => setDrawerOpen(true)} // Open the Drawer
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Drawer for Mobile */}
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)} // Close the Drawer
        >
          <Box
            sx={{
              width: "210px",
              mx: "30px",
              mt: "45px",
              mb: 1,
              cursor: "pointer",
            }}
          >
            <img
              src={darkMode ? LogoDark : Logo}
              alt="3D Practice Logo"
              onClick={() => {
                navigate("/");
                setDrawerOpen(false); // Close the Drawer after navigation
              }}
            />
          </Box>
          <List sx={{ mx: "22px" }}>
            {menuItems.slice(1).map((item, index) => (
              <ListItem
                key={index}
                selected={currentSection === item.id}
                onClick={() => handleNavigation(item)}
                sx={{
                  fontSize: "20px",
                  height: "52px",
                  my: 1,
                  background:
                    currentSection === item.id
                      ? getColors.listItemHoverBg
                      : "transparent",
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: "40px",
                    "&.MuiListItemIcon-root": {
                      "& svg": {
                        filter:
                          currentSection === item.id
                            ? "brightness(0) invert(1)"
                            : darkMode
                            ? "brightness(0.7) invert(1)"
                            : "none",
                      },
                    },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{
                    "& .MuiListItemText-primary": {
                      color:
                        currentSection === item.id && getColors.primaryTextDark,
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Drawer>
      </div>
    );
  }

  // Render Sidebar for larger screens
  return (
    <Drawer
      variant="permanent"
      sx={{ display: showSidebar ? "block" : "none" }}
    >
      <Box
        sx={{
          width: "210px",
          mx: "30px",
          mt: "45px",
          mb: "24px",
          cursor: "pointer",
        }}
      >
        <img
          src={darkMode ? LogoDark : Logo}
          alt="3D Practice Logo"
          onClick={() => navigate("/")}
        />
      </Box>
      <List sx={{ mx: "22px" }}>
        {menuItems.slice(1).map((item, index) => (
          <ListItem
            key={index}
            selected={currentSection === item.id}
            onClick={() => handleNavigation(item)}
            sx={{
              fontSize: "20px",
              height: "52px",
              my: 1,
              background:
                currentSection === item.id
                  ? getColors.listItemHoverBg
                  : "transparent",
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: "40px",
                "&.MuiListItemIcon-root": {
                  "& svg": {
                    filter:
                      currentSection === item.id
                        ? "brightness(0) invert(1)"
                        : darkMode
                        ? "brightness(0.7) invert(1)"
                        : "none",
                  },
                },
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              sx={{
                "& .MuiListItemText-primary": {
                  color:
                    currentSection === item.id && getColors.primaryTextDark,
                },
              }}
            />
          </ListItem>
        ))}
      </List>
    </Drawer>
  );
};
